import React from "react";
import { useTranslation } from "react-i18next";
import {
	Building2,
	Clock,
	Coins,
	Monitor,
	Receipt,
	Scale,
	ShieldCheck,
	TrendingDown,
	TrendingUp,
	Trophy,
	UserCheck,
	Users,
} from "lucide-react";
import { reportsAPI, shiftsAPI } from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { usePermission } from "@/hooks/usePermission";
import { Price } from "@/components/ui/price";
import { cn } from "@/lib/utils";
import { ReportTable } from "../components/ReportTable";
import { RankedBars } from "../components/RankedBars";
import { StatTile } from "../components/StatTile";
import { ReportNote, ReportState, StatGrid, StatusBadge, useReportQuery } from "../components/ReportBlocks";
import { formatPercent, num, personName, roundTo, share, type ReportColumn } from "../lib/columns";
import type { ReportBodyProps } from "../types";
import { ROW_CAP } from "./sales";

const sumBy = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((s, r) => s + pick(r), 0);

/** Cash drawer variance: negative = short (less cash than expected), positive = over. */
function Variance({ value, currency }: { value: number; currency: string }) {
	const { t } = useTranslation();
	const v = roundTo(value, 2);
	if (v === 0) {
		return (
			<span className="text-muted-foreground">
				<Price value={0} currency={currency} />
			</span>
		);
	}
	return (
		<span
			className={cn("inline-flex items-center gap-1 font-semibold", v < 0 ? "text-destructive" : "text-warning")}
		>
			<Price value={v} currency={currency} />
			<span className="text-xs font-normal">
				{v < 0 ? t("reports.variance.short") : t("reports.variance.over")}
			</span>
		</span>
	);
}

const varianceTone = (v: number) => (v < 0 ? "negative" : v > 0 ? "warning" : "default");

/* ─────────────────────────────── Cashier performance ─────────────────────────────── */

export function EmployeesReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const canUsers = usePermission("users.read");
	const query = useReportQuery(
		"employees",
		params,
		() => reportsAPI.getEmployeesReport(params) as Promise<{ employees?: any[] }>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const rows = data?.employees ?? [];
				const total = sumBy(rows, r => num(r.totalAmount));
				const invoices = sumBy(rows, r => num(r.totalSales));
				const top = [...rows].sort((a, b) => num(b.totalAmount) - num(a.totalAmount))[0];
				const userHref = canUsers ? (r: any) => `/users/${r.id}` : undefined;
				const columns: ReportColumn<any>[] = [
					{ key: "rank", label: "#", value: r => num(r.rank), kind: "number", align: "center" },
					{ key: "name", label: t("reports.cols.employee"), value: r => personName(r) || `#${r.id}` },
					{
						key: "invoices",
						label: t("reports.cols.invoices"),
						value: r => num(r.totalSales),
						kind: "number",
						total: true,
					},
					{
						key: "avg",
						label: t("reports.cols.avgInvoice"),
						value: r => (num(r.totalSales) ? num(r.totalAmount) / num(r.totalSales) : 0),
						kind: "money",
						currency,
						hideOnMobile: true,
					},
					{
						key: "returns",
						label: t("reports.cols.returnsCount"),
						value: r => num(r.returnCount),
						kind: "number",
						total: true,
						hideOnMobile: true,
					},
					{
						key: "amount",
						label: t("reports.cols.salesAmount"),
						value: r => num(r.totalAmount),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "share",
						label: t("reports.cols.share"),
						value: r => share(num(r.totalAmount), total),
						kind: "percent",
						hideOnMobile: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.sales")}
								value={total}
								format="money"
								currency={currency}
							/>
							<StatTile icon={Users} label={t("reports.kpi.activeCashiers")} value={rows.length} />
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.avgInvoice")}
								value={invoices ? total / invoices : 0}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.topCashier")}
								value={top ? num(top.totalAmount) : null}
								format="money"
								currency={currency}
								hint={top ? personName(top) : undefined}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.salesByCashier")}
							currency={currency}
							showShare
							items={rows.map(r => ({
								key: r.id,
								label: personName(r) || `#${r.id}`,
								value: num(r.totalAmount),
								sub: t("reports.salesTrend.invoicesCount", { count: num(r.totalSales) }),
								href: userHref?.(r),
							}))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.id}
							rowHref={userHref}
							exportName={exportName}
							emptyIcon={Users}
							defaultSort={{ key: "amount", dir: "desc" }}
							cardTitleKey="name"
						/>
						<ReportNote>{t("reports.notes.employees")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Branch comparison ─────────────────────────────── */

export function BranchesReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery(
		"branches",
		params,
		() => reportsAPI.getBranchesReport(params) as Promise<{ branches?: any[] }>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const rows = data?.branches ?? [];
				const revenue = sumBy(rows, r => num(r.salesRevenue));
				const expenses = sumBy(rows, r => num(r.totalExpenses));
				const profit = sumBy(rows, r => num(r.profit));
				const best = [...rows].sort((a, b) => num(b.salesRevenue) - num(a.salesRevenue))[0];
				const hasBest = Boolean(best && num(best.salesRevenue) > 0);
				const columns: ReportColumn<any>[] = [
					{ key: "name", label: t("reports.cols.branch"), value: r => r.name },
					{
						key: "invoices",
						label: t("reports.cols.invoices"),
						value: r => num(r.salesCount),
						kind: "number",
						total: true,
					},
					{
						key: "revenue",
						label: t("reports.cols.revenue"),
						value: r => num(r.salesRevenue),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "avg",
						label: t("reports.cols.avgInvoice"),
						value: r => (num(r.salesCount) ? num(r.salesRevenue) / num(r.salesCount) : 0),
						kind: "money",
						currency,
						hideOnMobile: true,
					},
					{
						key: "expenses",
						label: t("reports.cols.expenses"),
						value: r => num(r.totalExpenses),
						kind: "money",
						currency,
						total: true,
						hideOnMobile: true,
					},
					{
						key: "profit",
						label: t("reports.cols.profit"),
						value: r => num(r.profit),
						kind: "money",
						currency,
						total: true,
						render: r => (
							<Price
								value={roundTo(num(r.profit), 2)}
								currency={currency}
								className={cn(num(r.profit) < 0 && "font-semibold text-destructive")}
							/>
						),
					},
					{
						key: "margin",
						label: t("reports.cols.marginPercent"),
						value: r => (num(r.salesRevenue) ? share(num(r.profit), num(r.salesRevenue)) : null),
						kind: "percent",
						hideOnMobile: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.sales")}
								value={revenue}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={TrendingDown}
								label={t("reports.kpi.totalExpenses")}
								value={expenses}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Scale}
								label={t("reports.kpi.branchProfit")}
								value={profit}
								format="money"
								currency={currency}
								tone={profit < 0 ? "negative" : "positive"}
								hint={
									revenue
										? t("reports.kpi.marginHint", { value: formatPercent(share(profit, revenue)) })
										: undefined
								}
							/>
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.topBranch")}
								value={hasBest ? num(best.salesRevenue) : null}
								format="money"
								currency={currency}
								hint={hasBest ? best.name : undefined}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.revenueByBranch")}
							currency={currency}
							showShare
							items={rows.map(r => ({ key: r.id, label: r.name, value: num(r.salesRevenue) }))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.id}
							exportName={exportName}
							emptyIcon={Building2}
							searchable={rows.length > 6}
							defaultSort={{ key: "revenue", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.branches")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Terminals ─────────────────────────────── */

export function TerminalsReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const canPos = usePermission("pos.read");
	const query = useReportQuery(
		"terminals",
		params,
		() => reportsAPI.getPosSummaryReport(params) as Promise<{ pos?: any[] }>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const rows = data?.pos ?? [];
				const sales = sumBy(rows, r => num(r.totalSales));
				const variance = sumBy(rows, r => num(r.variance));
				const openNow = sumBy(rows, r => num(r.openShifts));
				const columns: ReportColumn<any>[] = [
					{ key: "name", label: t("reports.cols.terminal"), value: r => r.name },
					{
						key: "branch",
						label: t("reports.cols.branch"),
						value: r => r.branchName || "",
						hideOnMobile: true,
					},
					{
						key: "sales",
						label: t("reports.cols.revenue"),
						value: r => num(r.totalSales),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "open",
						label: t("reports.cols.openShifts"),
						value: r => num(r.openShifts),
						kind: "number",
						total: true,
						hideOnMobile: true,
					},
					{
						key: "closed",
						label: t("reports.cols.closedShifts"),
						value: r => num(r.closedShifts),
						kind: "number",
						total: true,
					},
					{
						key: "variance",
						label: t("reports.cols.cashVariance"),
						value: r => num(r.variance),
						kind: "money",
						currency,
						total: true,
						render: r => <Variance value={num(r.variance)} currency={currency} />,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.sales")}
								value={sales}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Monitor}
								label={t("reports.kpi.terminals")}
								value={rows.length}
								hint={t("reports.kpi.terminalsWithSales", {
									count: rows.filter(r => num(r.totalSales) > 0).length,
								})}
							/>
							<StatTile
								icon={Clock}
								label={t("reports.kpi.shiftsInPeriod")}
								value={sumBy(rows, r => num(r.openShifts) + num(r.closedShifts))}
								hint={t("reports.kpi.openNow", { count: openNow })}
							/>
							<StatTile
								icon={Coins}
								label={t("reports.kpi.cashVariance")}
								value={variance}
								format="money"
								currency={currency}
								tone={varianceTone(variance)}
								hint={t("reports.variance.explain")}
							/>
						</StatGrid>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.id}
							rowHref={canPos ? r => `/pos/${r.id}` : undefined}
							exportName={exportName}
							emptyIcon={Monitor}
							defaultSort={{ key: "sales", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.terminals")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Shifts & cash reconciliation ─────────────────────────────── */

export function ShiftsReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const q = {
		page: 1,
		limit: ROW_CAP,
		branchId: params.branchId ? Number(params.branchId) : undefined,
		posId: params.posId ? Number(params.posId) : undefined,
		// The shifts endpoint compares raw timestamps, so the whole last day is sent explicitly.
		startDate: params.startDate ? `${params.startDate}T00:00:00` : undefined,
		endDate: params.endDate ? `${params.endDate}T23:59:59.999` : undefined,
	};
	const query = useReportQuery("shifts", q, () => shiftsAPI.getAll(q) as Promise<{ shifts?: any[]; total?: number }>);
	return (
		<ReportState query={query}>
			{data => {
				const rows = data?.shifts ?? [];
				const closed = rows.filter(s => s.status === "closed");
				const variance = sumBy(closed, s => num(s.variance));
				const short = closed.filter(s => num(s.variance) < 0);
				const statusKey = (s: any) => (s.status === "open" ? "open" : "closed");
				const money = (pick: (s: any) => unknown) => (s: any) => (pick(s) == null ? null : num(pick(s)));
				const columns: ReportColumn<any>[] = [
					{
						key: "terminal",
						label: t("reports.cols.terminal"),
						value: s => s.pos?.name || s.branch?.name || `#${s.id}`,
					},
					{
						key: "openedBy",
						label: t("reports.cols.openedBy"),
						value: s => personName(s.openedBy),
						hideOnMobile: true,
					},
					{ key: "openedAt", label: t("reports.cols.openedAt"), value: s => s.openedAt, kind: "datetime" },
					{
						key: "closedAt",
						label: t("reports.cols.closedAt"),
						value: s => s.closedAt,
						kind: "datetime",
						hideOnMobile: true,
					},
					{
						key: "status",
						label: t("reports.cols.status"),
						value: s => t(`reports.shiftStatus.${statusKey(s)}`),
						render: s => (
							<StatusBadge
								status={s.status === "open" ? "pending" : "completed"}
								label={t(`reports.shiftStatus.${statusKey(s)}`)}
							/>
						),
						align: "center",
					},
					{
						key: "expected",
						label: t("reports.cols.expectedCash"),
						value: money(s => s.expectedCash),
						kind: "money",
						currency,
					},
					{
						key: "counted",
						label: t("reports.cols.countedCash"),
						value: money(s => s.closingCash),
						kind: "money",
						currency,
					},
					{
						key: "variance",
						label: t("reports.cols.cashVariance"),
						value: money(s => s.variance),
						kind: "money",
						currency,
						total: true,
						render: s =>
							s.variance == null ? (
								<span className="text-muted-foreground">-</span>
							) : (
								<Variance value={num(s.variance)} currency={currency} />
							),
					},
					{
						key: "opening",
						label: t("reports.cols.openingCash"),
						value: money(s => s.openingCash),
						kind: "money",
						currency,
						hideOnMobile: true,
					},
					{
						key: "cashSales",
						label: t("reports.cols.cashSales"),
						value: money(s => s.cashSalesTotal),
						kind: "money",
						currency,
						total: true,
						hideOnMobile: true,
					},
					{
						key: "network",
						label: t("reports.cols.cardSales"),
						value: money(s => s.networkTotal),
						kind: "money",
						currency,
						total: true,
						hideOnMobile: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={Clock}
								label={t("reports.kpi.shiftsInPeriod")}
								value={rows.length}
								hint={t("reports.kpi.openNow", { count: rows.length - closed.length })}
							/>
							<StatTile icon={UserCheck} label={t("reports.kpi.closedShifts")} value={closed.length} />
							<StatTile
								icon={Coins}
								label={t("reports.kpi.cashVariance")}
								value={variance}
								format="money"
								currency={currency}
								tone={varianceTone(variance)}
								hint={t("reports.variance.explain")}
							/>
							<StatTile
								icon={TrendingDown}
								label={t("reports.kpi.shortShifts")}
								value={short.length}
								tone={short.length ? "negative" : "default"}
								hint={
									short.length ? (
										<>
											{t("reports.kpi.shortTotal")}{" "}
											<Price
												value={roundTo(
													sumBy(short, s => num(s.variance)),
													2,
												)}
												currency={currency}
											/>
										</>
									) : undefined
								}
							/>
						</StatGrid>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={s => s.id}
							exportName={exportName}
							emptyIcon={Clock}
							truncated={{ shown: rows.length, total: num(data?.total) }}
							defaultSort={{ key: "openedAt", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.shifts")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Roles ─────────────────────────────── */

export function RolesReport({ exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const query = useReportQuery("roles", {}, () => reportsAPI.getRolesSummaryReport() as Promise<{ roles?: any[] }>);
	return (
		<ReportState query={query} tiles={2}>
			{data => {
				const rows = data?.roles ?? [];
				const columns: ReportColumn<any>[] = [
					{ key: "name", label: t("reports.cols.role"), value: r => r.name },
					{
						key: "users",
						label: t("reports.cols.users"),
						value: r => num(r.userCount),
						kind: "number",
						total: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid className="xl:grid-cols-2">
							<StatTile icon={ShieldCheck} label={t("reports.kpi.roles")} value={rows.length} />
							<StatTile
								icon={Users}
								label={t("reports.kpi.users")}
								value={sumBy(rows, r => num(r.userCount))}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.usersByRole")}
							format="number"
							items={rows.map(r => ({ key: r.id, label: r.name, value: num(r.userCount) }))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.id}
							exportName={exportName}
							emptyIcon={ShieldCheck}
							searchable={false}
						/>
					</div>
				);
			}}
		</ReportState>
	);
}
